import { Config } from "@/components/crud/crud-config";
import CrudFormElements from "@/components/crud/crud-form-elements";
import CrudPage from "@/components/crud/page";
import PencilIcon from "@/components/icons/pencil";
import TableActions from "@/components/table-actions";
import PermissionsLists from "@/config/PermissionsLists";
import useQueryKeys from "@/config/query-keys";
import { EndPoints, httpService, Operation } from "@/Core/Client";
import { DATE_FORMAT_OPTIONS } from "@/Core/Helpers/DayjsHelpers";
import DateCell from "@/ui/date-cell";
import { UtilFormDto, utilFormSchema, UtilResponseDto } from "@Dtos/util-dto";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Checkbox, Text } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";

type responseDto = UtilResponseDto;
type formType = UtilFormDto;

const config: Config<responseDto, formType> = {
    endPoint: EndPoints.category.crud,
    queryKey: useQueryKeys.category,
    columns: [],
    validationSchema: utilFormSchema,
    fields: [
        {
            name: "name",
            label: "Name",
            type: "text",
            hasFilter: true,
            className: "col-span-full",
            filter: {
                operation: Operation.eq,
            },
        },
        {
            name: "description",
            label: "Description",
            type: "textarea",
            className: "col-span-full",
            props: {
                textareaClassName: "h-20",
            },
        },
        {
            name: "createdAt",
            label: "Created At",
            type: "date",
            hasFilter: true,
            filterOnly: true,
            dateFormat: DATE_FORMAT_OPTIONS.L,
            // className: "w-auto",
            filter: {
                operation: Operation.gte,
                // showOperations: true,
            },
        },
    ],
    titles: {
        pageTitle: "Categories",
    },
    filterProps: {
        useDrawer: true,
        ignoreSorting: false,
    },

    permissionsCreate: PermissionsLists.Category.Create,
    formOptions: {
        customSize: "750px",
    },
};

const columnHelper = createColumnHelper<responseDto>();

export const columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),
    columnHelper.display({
        id: "id",
        size: 100,
        header: "#",
        cell: ({ row }) => <>{row.index + 1}</>,
    }),
    columnHelper.accessor("name", {
        id: "name",
        size: 130,
        header: "Name",
        cell: ({ row: { original } }) => <Text>{original.name}</Text>,
    }),
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        size: 130,
        header: "Created",
        cell: ({ row }) => (
            <DateCell
                date={new Date(Date.parse(row.original?.createdAt ?? ""))}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();

            return (
                <TableActions
                    row={row.original}
                    onDelete={async () => {
                        setLoading(true);

                        await httpService(config.endPoint).delete(
                            row.original.id!,
                        );

                        await queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });

                        setLoading(false);
                    }}
                    actions={[
                        {
                            title: "View",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <PencilIcon className="size-4" />,
                            onClick: () =>
                                openModal({
                                    view: (
                                        <CrudFormElements<responseDto, formType>
                                            editData={row.original}
                                            config={config}
                                        />
                                    ),
                                    customSize: config.formOptions?.customSize,
                                }),
                        },
                    ]}
                />
            );
        },
    }),
];

config.columns = columns;

export default function MainPage() {
    return CrudPage<responseDto, formType>({ config: config });
}
